import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Page Dashboard de Monitoring intégrée dans React
 * Consomme l'API backend avec authentification automatique
 */
const MonitoringDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLogType, setSelectedLogType] = useState(null); // Type de log sélectionné
  const [displayedLogs, setDisplayedLogs] = useState([]); // Logs actuellement affichés
  const [logOffset, setLogOffset] = useState(0); // Offset pour pagination
  const [loadingMore, setLoadingMore] = useState(false); // Chargement de plus de logs
  const { user } = useAuth();

  /**
   * Charger les données du dashboard
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Démarrage chargement dashboard...');
      console.log('🌐 URL complète:', `/admin/monitoring/dashboard?limit=200`);
      console.log('👤 Utilisateur:', user);
      
      const response = await api.get(`/admin/monitoring/dashboard?limit=200`);
      console.log('✅ Réponse API complète:', response);
      console.log('📊 Données reçues:', response.data);
      
      // L'API backend retourne { success: true, data: { stats, recentActivity, ... } }
      if (response.data && response.data.success) {
        const apiData = response.data.data;
        console.log('📋 API Data extraite:', apiData);
        
        const processedData = {
          stats: apiData.stats || {},
          recentLogs: apiData.recentActivity || [], // recentActivity contient les logs
          recentErrors: apiData.recentErrors || [],
          performanceIssues: apiData.performanceIssues || [],
          securityEvents: apiData.securityEvents || []
        };
        
        console.log('🎯 Données traitées:', processedData);
        setDashboardData(processedData);
      } else {
        console.error('❌ Format de réponse invalide:', response.data);
        setError('Format de données invalide - Voir console pour détails');
      }
    } catch (err) {
      console.error('❌ Erreur complète:', err);
      console.error('📋 Response:', err.response);
      console.error('📋 Request:', err.request);
      console.error('📋 Config:', err.config);
      
      let errorMessage = 'Erreur de chargement des données';
      
      if (err.response) {
        console.error('🔴 Erreur de réponse serveur:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers
        });
        errorMessage = `Erreur ${err.response.status}: ${err.response.data?.message || err.response.statusText}`;
      } else if (err.request) {
        console.error('🔴 Erreur de requête:', err.request);
        errorMessage = 'Impossible de contacter le serveur';
      } else {
        console.error('🔴 Erreur de configuration:', err.message);
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Effet pour charger les données au montage
   */
  useEffect(() => {
    loadDashboardData();
  }, []);

  /**
   * Auto-refresh toutes les 30 secondes
   */
  useEffect(() => {
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Charger les logs d'un type spécifique avec pagination
   */
  const loadLogsOfType = (logType, offset = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      }
      
      let logsToShow = [];
      
      switch (logType) {
        case 'all':
          logsToShow = [
            ...(dashboardData.recentLogs || []),
            ...(dashboardData.recentErrors || []),
            ...(dashboardData.performanceIssues || []),
            ...(dashboardData.securityEvents || [])
          ];
          break;
        case 'error':
          logsToShow = dashboardData.recentErrors || [];
          break;
        case 'info':
          logsToShow = (dashboardData.recentLogs || []).filter(log => 
            log.level?.toLowerCase().includes('info')
          );
          break;
        case 'performance':
          logsToShow = dashboardData.performanceIssues || [];
          break;
        case 'security':
          logsToShow = dashboardData.securityEvents || [];
          break;
        default:
          logsToShow = dashboardData.recentLogs || [];
      }
      
      // Pagination : 50 logs par page
      const pageSize = 50;
      const startIndex = offset;
      const endIndex = offset + pageSize;
      const newLogs = logsToShow.slice(startIndex, endIndex);
      
      if (append && displayedLogs.length > 0) {
        setDisplayedLogs(prev => [...prev, ...newLogs]);
      } else {
        setDisplayedLogs(newLogs);
      }
      
      setLogOffset(endIndex);
      
    } catch (err) {
      console.error('Erreur lors du chargement des logs:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * Gérer le clic sur une carte statistique
   */
  const handleCardClick = (logType) => {
    console.log(`🔍 Carte cliquée: ${logType}`);
    setSelectedLogType(logType);
    setLogOffset(0);
    loadLogsOfType(logType, 0, false);
  };

  /**
   * Charger plus de logs (pagination)
   */
  const loadMoreLogs = () => {
    if (selectedLogType) {
      loadLogsOfType(selectedLogType, logOffset, true);
    }
  };

  /**
   * Fermer la vue des logs
   */
  const closeLogs = () => {
    setSelectedLogType(null);
    setDisplayedLogs([]);
    setLogOffset(0);
  };



  /**
   * Obtenir la couleur selon le niveau de log
   */
  const getLogLevelColor = (level) => {
    // Gestion flexible du niveau
    const logLevel = (level || '').toString().toLowerCase();
    console.log(`🎨 Log level pour couleur: "${logLevel}"`);
    
    if (logLevel.includes('error')) return 'error';
    if (logLevel.includes('warn')) return 'warning'; 
    if (logLevel.includes('info')) return 'info';
    return 'default';
  };

  /**
   * Obtenir l'icône selon le niveau de log
   */
  const getLogLevelIcon = (level) => {
    const logLevel = (level || '').toString().toLowerCase();
    console.log(`🔍 Log level pour icône: "${logLevel}"`);
    
    if (logLevel.includes('error')) return <ErrorIcon />;
    if (logLevel.includes('warn')) return <WarningIcon />;
    if (logLevel.includes('info')) return <InfoIcon />;
    return <TimelineIcon />;
  };

  if (loading && !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Chargement du dashboard monitoring...
        </Typography>
      </Box>
    );
  }

      if (error) {
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">Erreur de chargement</Typography>
              <Typography>{error}</Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadDashboardData}
                  sx={{ mr: 1 }}
                >
                  Réessayer
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    console.log('🧪 Test direct API...');
                    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
                    window.open(`${apiBaseUrl}/admin/monitoring/dashboard`, '_blank');
                  }}
                  sx={{ mr: 1 }}
                >
                  Test API Direct
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    console.log('👤 Utilisateur connecté:', user);
                    console.log('🔑 Token disponible:', !!localStorage.getItem('token'));
                    console.log('🌐 Base URL API:', api.defaults?.baseURL);
                  }}
                >
                  Debug Info
                </Button>
              </Box>
            </Alert>
          </Box>
        );
      }
      
      const stats = dashboardData?.stats || {};

      // Debug pour voir les données (simplifié)
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Dashboard Stats:', stats);
        console.log('📋 Selected log type:', selectedLogType);
        console.log('📋 Displayed logs:', displayedLogs.length);
      }

  return (
    <Box>
      {/* En-tête */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          📊 Dashboard de Monitoring
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Actualiser'}
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" paragraph>
        Surveillance en temps réel des logs système - Dernière mise à jour: {new Date().toLocaleString('fr-FR')}
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'all' ? 2 : 0,
              borderColor: selectedLogType === 'all' ? 'primary.main' : 'transparent'
            }}
            onClick={() => handleCardClick('all')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TimelineIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.total || 0}
              </Typography>
              <Typography color="textSecondary">
                Total Logs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'error' ? 2 : 0,
              borderColor: selectedLogType === 'error' ? 'error.main' : 'transparent'
            }}
            onClick={() => handleCardClick('error')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {stats.errors || 0}
              </Typography>
              <Typography color="textSecondary">
                Erreurs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'warn' ? 2 : 0,
              borderColor: selectedLogType === 'warn' ? 'warning.main' : 'transparent'
            }}
            onClick={() => handleCardClick('warn')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {stats.warnings || 0}
              </Typography>
              <Typography color="textSecondary">
                Avertissements
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'performance' ? 2 : 0,
              borderColor: selectedLogType === 'performance' ? 'secondary.main' : 'transparent'
            }}
            onClick={() => handleCardClick('performance')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <SpeedIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                {stats.slowOperations || 0}
              </Typography>
              <Typography color="textSecondary">
                Requêtes Lentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'security' ? 2 : 0,
              borderColor: selectedLogType === 'security' ? 'warning.main' : 'transparent'
            }}
            onClick={() => handleCardClick('security')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <SecurityIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {stats.securityEvents || 0}
              </Typography>
              <Typography color="textSecondary">
                Événements Sécurité
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              '&:hover': { 
                transform: 'translateY(-4px)', 
                boxShadow: 4,
                backgroundColor: 'action.hover'
              },
              border: selectedLogType === 'info' ? 2 : 0,
              borderColor: selectedLogType === 'info' ? 'info.main' : 'transparent'
            }}
            onClick={() => handleCardClick('info')}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <InfoIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {stats.info || 0}
              </Typography>
              <Typography color="textSecondary">
                Informations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section d'affichage des logs sélectionnés */}
      {selectedLogType && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6">
              Logs - {selectedLogType === 'all' ? 'Tous les types' : 
                     selectedLogType === 'error' ? 'Erreurs' :
                     selectedLogType === 'warn' ? 'Avertissements' :
                     selectedLogType === 'info' ? 'Informations' :
                     selectedLogType === 'performance' ? 'Performance' :
                     selectedLogType === 'security' ? 'Sécurité' : selectedLogType}
              {displayedLogs.length > 0 && ` (${displayedLogs.length} affichés)`}
            </Typography>
            <IconButton onClick={closeLogs} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Niveau</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      {loadingMore ? (
                        <Box display="flex" justifyContent="center" alignItems="center">
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Chargement des logs...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Aucun log trouvé pour cette catégorie
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedLogs.map((log, index) => (
                    <TableRow 
                      key={`${log.timestamp}-${index}`} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      <TableCell sx={{ minWidth: 160, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }) : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Chip 
                          icon={getLogLevelIcon(log.level)}
                          label={log.level || 'unknown'}
                          color={getLogLevelColor(log.level)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.875rem',
                        maxWidth: '600px',
                        wordBreak: 'break-word'
                      }}>
                        {log.message || 'Aucun message'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Bouton "Charger plus" */}
          {selectedLogType && displayedLogs.length > 0 && displayedLogs.length >= 50 && (
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Button 
                variant="outlined" 
                onClick={loadMoreLogs}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              >
                {loadingMore ? 'Chargement...' : 'Charger plus de logs'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MonitoringDashboard;
